import React, { useCallback, useEffect, useMemo, useState } from 'react'
import log from 'loglevel'
import { Chat } from '@trustero/trustero-api-web/lib/chat/chat_pb'
import { useUser } from 'src/components/async/Users/useUser'
import { TooltipIconButton } from 'src/components/Reusable/Buttons/TooltipIconButton'
import {
  CheckSVG,
  EyeInvisibleSVG,
  EyeVisibleSVG,
  PrivateSVG,
  PublicSVG,
} from 'src/components/Icons/Basic'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import { Dropdown } from 'react-bootstrap'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox/CheckBoxInput'
import { P } from 'src/components/Reusable/Text/Text.styles'
import { FilterDropdownToggle } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.styles'
import { useIsInternalUser } from 'src/context/authContext'
import { useAgentChat, useConfigLLMModel } from 'src/context/Gatekeeper'
import { useUpdateChat } from '../trusteroIntelligence.hooks'
import { ChatPrivateSelector } from './chatPanel.styles'

export type ChatVisibilityProps = {
  chat?: Chat
  isPrivate: boolean
  setIsPrivate: (isPrivate: boolean) => void
  type?: 'icon' | 'checkbox'
}

export const ChatVisibility = ({
  chat,
  isPrivate,
  setIsPrivate,
  type = 'icon',
}: ChatVisibilityProps): JSX.Element => {
  const showAgentChat = useAgentChat()
  const [model, setModel] = useState(chat?.getModel() || 'gpt-4o')
  const isInternalUser = useIsInternalUser()
  const canConfigLLMModel = useConfigLLMModel()
  const updateChat = useUpdateChat()
  const user = useUser()
  const owner = chat?.getOwner()

  useEffect(() => {
    if (chat) {
      setModel(chat.getModel() || 'gpt-4o')
    }
  }, [chat])

  const isOwner = useMemo(
    () => owner && owner.getId() === user?.data?.getId(),
    [owner, user],
  )

  const saveChat = useCallback(
    async (chat: Chat) => {
      try {
        await updateChat(chat)
      } catch (error) {
        log.error('Error updating chat', error)
      }
    },
    [updateChat],
  )

  const onClick = useCallback(async () => {
    if (chat) {
      try {
        chat.setIsPrivate(!isPrivate)
        await saveChat(chat)
      } catch (error) {
        log.error('Error updating chat', error)
      }
    }
    setIsPrivate(!isPrivate)
  }, [chat, saveChat, isPrivate, setIsPrivate])

  const onModelChange = useCallback(
    async (newModel: string) => {
      if (chat) {
        chat.setModel(newModel)
        await saveChat(chat)
        setModel(newModel)
      }
    },
    [chat, saveChat],
  )

  const tooltipText = useMemo(() => {
    const messages = [`This is a ${isPrivate ? 'private' : 'public'} chat.`]
    if (isOwner || !chat) {
      messages.push(`Click to make it ${isPrivate ? 'public' : 'private'}.`)
    }
    return messages.join(' ')
  }, [isPrivate, isOwner, chat])

  const isDisabled = useMemo(() => {
    return chat && !isOwner
  }, [chat, isOwner])

  const privateIcon = useMemo(() => {
    if (showAgentChat) {
      return <PrivateSVG />
    }
    return <EyeInvisibleSVG />
  }, [showAgentChat])

  const publicIcon = useMemo(() => {
    if (showAgentChat) {
      return <PublicSVG />
    }
    return <EyeVisibleSVG />
  }, [showAgentChat])

  if (type === 'checkbox' && !showAgentChat) {
    return (
      <ChatVisibilityCheckbox
        isPrivate={isPrivate}
        onClick={onClick}
        disabled={isDisabled}
      />
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {isInternalUser && canConfigLLMModel && !showAgentChat && (
        <ModelSelector model={model} onChange={onModelChange} />
      )}
      <TooltipIconButton
        disabled={isDisabled}
        onClick={onClick}
        icon={isPrivate ? privateIcon : publicIcon}
        successIcon={<CheckSVG />}
        tooltipText={tooltipText}
        tooltipClickedText={`Chat is now ${isPrivate ? 'private' : 'public'}.`}
        placement="left"
        preserveIconStyles={!showAgentChat}
      />
    </div>
  )
}

type ChatVisibilityCheckboxProps = {
  isPrivate: boolean
  onClick: () => void
  disabled?: boolean
}

export const ChatVisibilityCheckbox = ({
  isPrivate,
  onClick,
  disabled,
}: ChatVisibilityCheckboxProps): JSX.Element => {
  return (
    <ChatPrivateSelector>
      <CheckBoxInput
        id={'private-chat-checkbox'}
        checked={isPrivate}
        onClick={(e) => e.stopPropagation()}
        onChange={onClick}
        disabled={disabled}
      />
      Private
    </ChatPrivateSelector>
  )
}

const MODEL_OPTIONS = [
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-4o-mini', label: 'GPT-4o-mini' },
  { value: 'gpt-5', label: 'GPT-5 (with reasoning)' },
  { value: 'gpt-5-mini', label: 'GPT-5-mini (cost effective)' },
  { value: 'gpt-5-nano', label: 'GPT-5-nano (fast and cheap)' },
]

const ModelSelector = ({
  model,
  onChange,
}: {
  model: string
  onChange: (model: string) => void
}): JSX.Element => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Tooltip
        id={`model-selector-tooltip-${model}`}
        trigger={['hover', 'click']}
        placement="left"
        tooltipBody={`OpenAI Model: ${model}`}
      >
        <Dropdown id={`model-selector-menu-${model}`}>
          <Dropdown.Toggle as={FilterDropdownToggle}>
            <P $fontSize={12}>Model: {model}</P>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {MODEL_OPTIONS.map((option) => (
              <Dropdown.Item
                as="button"
                onClick={() => onChange(option.value)}
                key={option.value}
              >
                {option.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Tooltip>
    </div>
  )
}
